<template>
	<div ref="container" class="monaco-editor-container"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick, defineExpose, shallowRef } from 'vue'
import * as monaco from 'monaco-editor'

// Use the SAME monaco import as fmCompletionProvider.ts to avoid
// dual Monaco instances (which causes page lockup / infinite loops).
// Do NOT also use @monaco-editor/loader — it loads a second instance from CDN.

interface Props {
	modelValue?: string
	language?: string
	theme?: string
	options?: monaco.editor.IStandaloneEditorConstructionOptions
	completionProviders?: CompletionProviderConfig[]
}

export interface CompletionProviderConfig {
	triggerCharacters?: string[]
	provideCompletionItems: (
		model: monaco.editor.ITextModel,
		position: monaco.Position,
		context: monaco.languages.CompletionContext,
		token: monaco.CancellationToken
	) => monaco.languages.ProviderResult<monaco.languages.CompletionList>
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: '',
	language: 'javascript',
	theme: '',
	options: () => ({}),
	completionProviders: () => [],
})

const emit = defineEmits(['update:modelValue', 'change', 'ready'])

const container = ref<HTMLElement | null>(null)
const editor = shallowRef<monaco.editor.IStandaloneCodeEditor | null>(null)
const isReady = ref(false)
const disposables: monaco.IDisposable[] = []
let resizeObserver: ResizeObserver | null = null
let resizeRafId: number | null = null

// Detect dark mode from the document
const getEditorTheme = (): string => {
	if (props.theme) return props.theme
	return document.documentElement.classList.contains('dark') ? 'vs-dark' : 'vs'
}

defineExpose({
	getEditorInstance: () => editor.value,
	getMonaco: () => monaco,
	insertText: (text: string) => {
		if (!editor.value) return
		const selection = editor.value.getSelection()
		if (selection) {
			editor.value.executeEdits('', [{
				range: selection,
				text,
				forceMoveMarkers: true,
			}])
		}
	},
})

const registerCompletionProviders = () => {
	// Dispose previous providers
	disposables.forEach(d => d.dispose())
	disposables.length = 0

	for (const provider of props.completionProviders) {
		const disposable = monaco.languages.registerCompletionItemProvider(props.language, {
			triggerCharacters: provider.triggerCharacters || ['<', ':', ' '],
			provideCompletionItems: provider.provideCompletionItems,
		})
		disposables.push(disposable)
	}
}

// Initialize Monaco
onMounted(async () => {
	// Wait for DOM layout to settle so container has computed dimensions
	await nextTick()

	if (!container.value) return

	editor.value = monaco.editor.create(container.value, {
		value: props.modelValue,
		language: props.language,
		// IMPORTANT: Do NOT use automaticLayout: true — it causes infinite resize loops
		// in flex containers where height:100% depends on content. We handle resizing
		// manually with a debounced ResizeObserver instead.
		automaticLayout: false,
		minimap: { enabled: false },
		fontSize: 14,
		theme: getEditorTheme(),
		tabSize: 2,
		wordWrap: 'on',
		suggest: {
			showWords: true,
			showSnippets: true,
		},
		quickSuggestions: {
			other: true,
			comments: false,
			strings: true,
		},
		...props.options,
	})

	isReady.value = true

	editor.value.onDidChangeModelContent(() => {
		const value = editor.value?.getValue() || ''
		emit('update:modelValue', value)
		emit('change', value)
	})

	// Register any custom completion providers
	registerCompletionProviders()

	// Manual resize handling with ResizeObserver (replaces automaticLayout).
	// Uses requestAnimationFrame to debounce and avoid layout thrashing.
	resizeObserver = new ResizeObserver(() => {
		if (resizeRafId) cancelAnimationFrame(resizeRafId)
		resizeRafId = requestAnimationFrame(() => {
			if (editor.value && container.value) {
				const rect = container.value.getBoundingClientRect()
				if (rect.width > 0 && rect.height > 0) {
					editor.value.layout({ width: rect.width, height: rect.height })
				}
			}
		})
	})
	resizeObserver.observe(container.value)

	// Initial layout after a short delay to ensure container is fully sized
	setTimeout(() => {
		if (editor.value && container.value) {
			const rect = container.value.getBoundingClientRect()
			if (rect.width > 0 && rect.height > 0) {
				editor.value.layout({ width: rect.width, height: rect.height })
			}
		}
	}, 150)

	emit('ready', editor.value)
})

// Watch for modelValue changes
watch(() => props.modelValue, (newValue) => {
	if (isReady.value && editor.value && newValue !== editor.value.getValue()) {
		editor.value.setValue(newValue)
	}
})

// Watch for language changes
watch(() => props.language, (newLanguage) => {
	if (isReady.value && editor.value) {
		const model = editor.value.getModel()
		if (model) {
			monaco.editor.setModelLanguage(model, newLanguage)
		}
	}
})

// Watch for completion provider changes
watch(() => props.completionProviders, () => {
	if (isReady.value) {
		registerCompletionProviders()
	}
}, { deep: true })

// Watch for dark mode changes
const themeObserver = new MutationObserver(() => {
	if (editor.value) {
		monaco.editor.setTheme(getEditorTheme())
	}
})

onMounted(() => {
	themeObserver.observe(document.documentElement, {
		attributes: true,
		attributeFilter: ['class'],
	})
})

// Clean up
onBeforeUnmount(() => {
	themeObserver.disconnect()
	if (resizeObserver) {
		resizeObserver.disconnect()
		resizeObserver = null
	}
	if (resizeRafId) {
		cancelAnimationFrame(resizeRafId)
		resizeRafId = null
	}
	disposables.forEach(d => d.dispose())
	editor.value?.dispose()
})
</script>

<style scoped>
.monaco-editor-container {
	width: 100%;
	height: 100%;
	min-height: 400px;
}
</style>
